import { faBan, faCheck, faEdit, faSpinner } from "@fortawesome/sharp-regular-svg-icons";
import { Button } from "eone-ui/buttons/button";
import { FormButton } from "eone-ui/forms/form-button";
import { FormError } from "eone-ui/forms/form-error";
import { getDataTestId } from "eone-ui/functions/data-test-ids";
import { FontAwesome } from "eone-ui/icons/icons";
import { AuthenticatedUserSettings, UserSetting } from "./connect-fields.jsx";
import { getConnectorAuthErrorMessage } from "./connector-auth-errors.js";
import { isConnectBusy } from "./flow-helpers.js";
import { useConnectFlow } from "./use-connect-flow";
import "./connect-v2.css";

/**
 * @typedef {import("./use-connect-flow").UseConnectFlowProps} UseConnectFlowProps
 *
 * @param {UseConnectFlowProps & {
 *   OnCancel?: () => void,
 *   OnAuthenticatedSettingsChanged?: () => void,
 * }} props
 */
function ConnectComponent(props) {
    const connect = useConnectFlow(props);

    const hasAuthSettings = !!props.ConnectorAuth?.AuthenticatedSettings && props.ConnectorAuth.AuthenticatedSettings.length > 0;
    const busy = isConnectBusy(connect);
    const baseDataTestId = props.DataTestId || "connect-component";
    const getConnectComponentDataTestId = (...parts) => getDataTestId(baseDataTestId, ...parts);
    const getSettingTestIdPart = (setting) => setting?.Label || setting?.Name || "setting";

    const getUserSettings = () => {
        if (!props.ConnectorAuth?.UserSettings) return null;
        const readOnly = busy || (hasAuthSettings && connect.phase !== "initial" && !connect.isEditing);
        const userSettingsDataTestId = getConnectComponentDataTestId("user-settings");
        return (
            <div className={`pd-connect-v2-user-settings${readOnly && !connect.hasUnsavedChanges ? " pd-connect-v2-user-settings-dimmed" : ""}`} data-testid={userSettingsDataTestId}>
                {props.ConnectorAuth.UserSettings.map((setting, index) => (
                    <UserSetting
                        key={setting.Name}
                        Name={setting.Name}
                        Label={setting.Label}
                        Placeholder={setting.Placeholder}
                        TooltipString={setting.Tooltip}
                        Value={connect.values[setting.Name] || ""}
                        Type={setting.FieldType}
                        Required={setting.Required}
                        Options={setting.Options}
                        Description={setting.Description}
                        ErrorMessage={connect.errors[setting.Name]}
                        ReadOnly={readOnly}
                        AutoFocus={index === 0}
                        Loading={false}
                        OnUpdate={connect.changeSetting}
                        OnChange={connect.changeSetting}
                        DataTestId={getDataTestId(userSettingsDataTestId, getSettingTestIdPart(setting))}
                    />
                ))}
            </div>
        );
    };

    const getConnectionStatus = () => {
        if (connect.phase === "initial") return null;
        const showSuccess = connect.phase === "connected" && !connect.hasUnsavedChanges;
        const showChanged = connect.hasUnsavedChanges;
        if (!showSuccess && !showChanged && !hasAuthSettings) return null;

        return (
            <div className="pd-connect-v2-status-bar" data-testid={getConnectComponentDataTestId("status")}>
                {showChanged ? (
                    <div>{"Connection settings changed"}</div>
                ) : showSuccess ? (
                    <div>
                        <FontAwesome Icon={faCheck} />
                        <span className="pd-connect-v2-status-text">{"Connected successfully"}</span>
                    </div>
                ) : (
                    <div />
                )}
                {hasAuthSettings && (
                    <div>
                        <Button Visible={connect.isEditing && !busy} Label="Cancel edit" Icon={faBan} OnClick={connect.cancelEdit} DataTestId={getConnectComponentDataTestId("status", "cancel-edit")} />
                        <Button Visible={!connect.isEditing && !busy} Label="Edit Connection" Icon={faEdit} OnClick={connect.editConnection} DataTestId={getConnectComponentDataTestId("status", "edit-connection")} />
                    </div>
                )}
            </div>
        );
    };

    // Changed connection settings must re-authenticate before anything is saved: the staged session
    // holds the credentials from the previous authentication, and connect-with-settings only reads the
    // authenticated settings from the body. Unchanged, an authenticated flow saves its settings — for
    // OAuth too, or the button would just send the user round the provider again.
    const needsReauthentication = connect.phase === "initial" || connect.hasUnsavedChanges;

    const handleMainClick = async () => {
        if (!needsReauthentication && connect.phase === "authenticated" && props.ConnectWithSettingsUrl) {
            await connect.handleSaveSettings();
        } else if (props.ConnectorAuth?.HasRedirectUri) {
            await connect.handleAuthorize();
        } else {
            await connect.handleConnect();
        }
    };

    const getConnectButtonProps = () => {
        if (connect.isSubmitting) {
            return {
                Label: connect.phase === "authenticated" ? "Connecting..." : "Authenticating...",
                Icon: faSpinner,
                IconSpin: true,
                Disabled: true,
            };
        }

        let label = "Connect";
        if (needsReauthentication && (props.ConnectorAuth?.HasRedirectUri || hasAuthSettings)) {
            label = "Authenticate";
        } else if (connect.phase === "connected") {
            label = "Reconnect";
        }

        return {
            Label: label,
            Class: "eone-ui-form-button-primary",
            OnClick: handleMainClick,
            Disabled: !connect.canConnect || (connect.isEditing && !connect.hasUnsavedChanges),
        };
    };

    const getValidateButtonProps = () => {
        if (!props.ValidateUrl) return null;
        if (connect.isValidating) {
            return { Label: "Validating...", Icon: faSpinner, IconSpin: true, Disabled: true };
        }
        return {
            Label: "Validate",
            OnClick: connect.handleValidate,
            Disabled: !connect.canConnect || (connect.isEditing && !connect.hasUnsavedChanges),
        };
    };

    const connectButtonProps = getConnectButtonProps();
    const validateButtonProps = getValidateButtonProps();

    return (
        <>
            {getConnectionStatus()}
            {getUserSettings()}
            {connect.phase !== "initial" && (
                <AuthenticatedUserSettings
                    AuthenticatedSettings={props.ConnectorAuth?.AuthenticatedSettings}
                    Values={connect.values}
                    Options={connect.authSettingsOptions}
                    Loading={connect.authSettingsLoading}
                    Disabled={connect.isEditing || busy}
                    OnSettingChange={connect.changeAuthSetting}
                    DataTestId={getConnectComponentDataTestId("auth-settings")}
                />
            )}
            <div className="pd-connect-v2-actions" data-testid={getConnectComponentDataTestId("actions")}>
                {props.OnCancel && <FormButton Label="Cancel" OnClick={props.OnCancel} DataTestId={getConnectComponentDataTestId("actions", "cancel")} />}
                {validateButtonProps && <FormButton {...validateButtonProps} DataTestId={getConnectComponentDataTestId("actions", "validate")} />}
                {connectButtonProps && <FormButton {...connectButtonProps} DataTestId={getConnectComponentDataTestId("actions", "connect")} />}
            </div>
            {connect.validationSuccess && (
                <div className="pd-connect-v2-validation-success" data-testid={getConnectComponentDataTestId("validation-success")}>
                    <FontAwesome Icon={faCheck} />
                    <span className="pd-connect-v2-status-text">{"Validation successful"}</span>
                </div>
            )}
            {/* The reason is a code (validation_failed); errorDescription below it is the service's
                own detail. Both are shown — neither is a substitute for the other. */}
            <FormError Message={getConnectorAuthErrorMessage(connect.error)} Visible={!!connect.error} DataTestId={getConnectComponentDataTestId("error")} />
            <FormError Message={connect.errorDescription} Visible={!!connect.errorDescription} DataTestId={getConnectComponentDataTestId("error-description")} />
        </>
    );
}

export { ConnectComponent };
